/**
 * What the account screen shows, for whichever record the open workspace is:
 * the person signed in, or the organisation they are standing in.
 *
 * That one distinction — `subjectOf` — decides which tabs exist and which
 * writer is behind the form, and the rest of the file turns raw account rows
 * into finished cards, rows and counts. The words are in
 * `profile.constants.ts`; the fetching and the drafts are in
 * `useProfileController.ts`.
 *
 * The rule most likely to be "fixed" by mistake is `addableMemberships`:
 * declared types are add-only, because `collector` is the sole route to the
 * collector capacity and unticking it would strand every holding behind a
 * checkbox. A grid of toggles is the obvious build here, and it would be wrong.
 */

import {
  AFFILIATION_AUDIENCES,
  MEMBERSHIP_LEVELS,
  ORG_PRIVILEGES,
  ORG_ROLES,
  affiliationStanding,
  isOrgRole,
  needsItsAnswerShown,
  normaliseAudiences,
  standsConfirmed,
} from "@ondo/shared";
import type {
  AffiliationAudience,
  AffiliationAudiences,
  AffiliationClaim,
  CollectionSummary,
  DeclaredMemberType,
  MeResponse,
  MembershipLevel,
  OrgMember,
  OrgPrivilege,
  OrgProfile,
  OrgRole,
  OrgRoleBundleLine,
  SeatInvitation,
  WorkspaceRef,
} from "@ondo/shared";

import type { ActivityRow } from "../dashboard/dashboard.model.ts";
import { MEMBERSHIP_TYPES, ONDO_ID_PATTERN } from "../lib/constants.ts";
import { initialsOf } from "../lib/names.ts";
import { workspacePath } from "../routes/routes.model.ts";
import {
  AFFILIATION_AUDIENCE_LABELS,
  AFFILIATION_STANDING_LABELS,
  MEMBERSHIP_LEVEL_LABELS,
  ORG_PRIVILEGE_LABELS,
  ORG_ROLE_LABELS,
  ORG_TYPE_LABELS,
  PROFILE_COPY,
  PROFILE_TABS,
} from "./profile.constants.ts";
import type { ProfileTabKey } from "./profile.constants.ts";

/**
 * Whose record this is.
 *
 * The one distinction the whole screen turns on, and it is `ondo.principal_kind`
 * rather than a role: a workspace with no organisation opens onto the person
 * signed in, and one with an organisation opens onto the organisation. The
 * prototype has the same split without naming it — its collector page is a
 * person and its estate page is an entity, with different fields and a different
 * writer for each.
 */
export type ProfileSubject = "person" | "organisation";

export function subjectOf(workspace: WorkspaceRef): ProfileSubject {
  return workspace.organisation === null ? "person" : "organisation";
}

export interface ProfileTab {
  key: ProfileTabKey;
  label: string;
  /** Rendered beside the label. Null for a tab with nothing to count. */
  count: number | null;
}

/** One field on the identity card: a small-caps label and a value. */
export interface Field {
  label: string;
  /** Already formatted. The view does no string work. */
  value: string;
  /** True when the registry holds nothing here, so it can be set apart. */
  absent?: boolean;
}

export interface IdentityCard {
  initials: string;
  name: string;
  /** The email for a person; the organisation's kind for an entity. */
  meta: string | null;
  verified: boolean;
  verifiedLabel: string;
  fields: Field[];
}

export interface MembershipChoice {
  label: string;
  /** The `DeclaredMemberType` behind the label, so a click can name it. */
  value: DeclaredMemberType;
  declared: boolean;
}

/** The editable half of Contact, as it sits in the form. */
export interface ContactValues {
  displayName: string;
  phone: string;
  country: string;
}

export interface AffiliationRow {
  claimId: string;
  /** "Hauser & Wirth · ONDO-6624" */
  subject: string;
  title: string | null;
  /** "Confirmed", "Disputed", "Awaiting an answer" — never a tally. */
  status: string;
  /** Whether the row is settled, so the view can tone it without a rule. */
  tone: "verified" | "contested" | "pending" | "quiet";
  /** Only a live, unanswered claim can be taken back. */
  canWithdraw: boolean;

  /**
   * What the other party said, and who said it.
   *
   * NOT OPTIONAL AND NOT BURIED, because OND-98 decision 3 is that the answer
   * travels with the claim wherever it is shown: "the dispute and its answer
   * must appear alongside the claim". The view has no branch that could draw
   * the row without this, which is why it is one object rather than a flag and
   * two strings the next edit could forget one of.
   */
  answer: { body: string; by: string | null } | null;
  /**
   * Which audiences see it, and whether this account may change them.
   *
   * Null when the server sent no audience — which happens on the INBOUND read
   * and must not be drawn as "hidden". Absent information and an empty audience
   * are different facts.
   */
  audiences: AffiliationAudiences | null;
  canPublish: boolean;
  /** The sentence under the controls: who this is currently shown to. */
  visibility: string;
  /** The party the claim was made by, when it was a firm rather than this person. */
  claimedFor: string | null;
}

export interface EntityRow {
  ondoId: string;
  name: string;
  initials: string;
  /** "Gallery · Director · Admin" */
  meta: string;
  verified: boolean;
  /** The workspace this membership opens, or null when none was derived. */
  href: string | null;
}

export interface HoldingRow {
  id: string;
  name: string;
  /** "6 works" */
  holdings: string;
  /** Owner, curator or viewer, in words. */
  level: string;
}

export interface TeamRow {
  /** The MEMBERSHIP's id, which is what ending a seat names. */
  membershipId: string;
  ondoId: string;
  name: string;
  initials: string;
  title: string | null;
  level: MembershipLevel;
  levelLabel: string;
  joined: string;
  /**
   * Whether this reader may end this seat.
   *
   * Straight from the server, never derived here. `ondo.org_members()` computes
   * it from the same three tests `ondo.end_membership()` applies — including
   * "you are the firm's only administrator", which needs a count across the
   * whole roster — and a second definition in the client would be the screen
   * offering a control the database refuses.
   */
  canEnd: boolean;
  /**
   * The reader's own row.
   *
   * Carried only so the ONE unexplained absence can be explained. A sole
   * administrator sees no control on their own seat, correctly, and would
   * otherwise go looking for it — the same confusion OND-100 is about, with the
   * button missing instead of dead. Every other reader who cannot end a seat is
   * a non-admin, and for them the absence IS the answer: a sentence on six rows
   * saying "you are not an administrator" is a screen apologising for itself.
   */
  isYou: boolean;
  /** The named role on this seat, or null when the rung answers alone. */
  role: OrgRole | null;
  /** "Registrar", or "No named role" — what the roster prints. */
  roleLabel: string;
  /**
   * What this seat can actually do, in the reader's words — the owner's "Show
   * the effective abilities".
   *
   * FROM THE SERVER'S RESOLVED LIST, never derived from `role` here. The firm
   * may have edited the bundle and the seat may carry an exception, so a label
   * computed from the role name would be wrong on exactly the seats somebody
   * took the trouble to customise. `ORG_PRIVILEGE_LABELS` turns the resolved
   * values into words and decides nothing.
   */
  abilities: string[];
  /** The privileges themselves, for the control that toggles one. */
  privileges: OrgPrivilege[];
  /**
   * Whether this seat's answers differ from what its bundle would say.
   *
   * It is the one fact an administrator cannot see from the abilities alone,
   * and the reason the three-valued exception exists at all: "this seat was
   * decided specially" and "this seat follows the bundle" are different facts
   * and a reader auditing a roster needs to know which rows to look at.
   *
   * Derived by comparing the resolved list with the firm's own grid, which is
   * why `toTeamRows` takes the bundles: it is not a second definition of the
   * rule — the server resolved both sides — it is a difference between two
   * server answers.
   */
  isCustomised: boolean;
  /** Whether THIS reader may set this seat's role or toggle its abilities. */
  canSetRole: boolean;
}

/** One cell of the firm's bundle grid, in the reader's words. */
export interface BundleCell {
  privilege: OrgPrivilege;
  label: string;
  granted: boolean;
  /** True when the firm has edited this line away from the delegated default. */
  isEdited: boolean;
}

/** One named role's bundle at this firm, as the customisation panel draws it. */
export interface BundleRow {
  role: OrgRole;
  label: string;
  cells: BundleCell[];
}

/**
 * Everything about the person, assembled once.
 *
 * Present even when the subject is an organisation, because "Your account" is a
 * tab there. That is not decoration: `deriveWorkspaces` gives a personal
 * workspace only to an account whose declared types produce one, so somebody who
 * declared "gallery" and holds a gallery membership may have no personal
 * workspace at all — and without this tab their own record would be unreachable
 * from anywhere in the app.
 */
export interface PersonPanels {
  identity: IdentityCard;
  /**
   * The account's own trail, from `GET /v1/activity?scope=me`.
   *
   * Built by the dashboard's `toActivity` rather than by a second formatter
   * here. One activity rendered two ways on two screens is one activity that
   * will eventually describe itself differently in each — and the dashboard
   * already owns the vocabulary for every `ActivityType`.
   */
  activity: ActivityRow[];
  membership: MembershipChoice[];
  /** Read-only display of the same three fields the form edits, plus email. */
  contact: Field[];
  values: ContactValues;
  summary: Field[];
  affiliations: AffiliationRow[];
}

export interface OrgPanels {
  identity: IdentityCard;
  values: { displayName: string; registeredAddress: string; registrationNumber: string };
  canEdit: boolean;
  team: TeamRow[];
  /**
   * What each named role means at this firm — OND-61.
   *
   * Always four rows, even for a firm that has never edited one, because the
   * panel's job is to say what the defaults ARE. An empty list would read as
   * "this firm has no roles" when what it means is "this firm has not changed
   * any of them".
   */
  bundles: BundleRow[];
  /**
   * What this firm has invited, and what became of each one — OND-24.
   *
   * EMPTY FOR A READER WHO MAY NOT ADMINISTER ITS STAFF, because that is what
   * the server answers them: the rows hold email addresses and sit behind
   * `ondo.may_administer_staff()` rather than at the `viewer` rung the roster
   * uses. `20261014092200` decision 9. The panel is not drawn in that case
   * either, so the two absences say the same thing.
   */
  invitations: InvitationRow[];
  /**
   * Whether this reader may change roles, abilities or bundles.
   *
   * Taken off the roster's `canSetRole` rather than computed from `canEdit` or
   * from a rung. `canEdit` is the admin rung and this is the rung AND the
   * `staff_admin` privilege — an admin-rung Registrar may change the firm's
   * registered address and may not touch its staff, which is the whole of what
   * "without staff/role administration" means in the owner's bundles.
   */
  canSetRoles: boolean;
  /**
   * The estate's three figures, or null for a firm that is not one.
   *
   * Null rather than three zeroes, and the distinction is the whole reason this
   * is nullable: a gallery has no catalogue raisonné, so "0 works" would be a
   * false statement about it rather than a true statement about an empty
   * catalogue. An estate whose catalogue is genuinely empty gets zeroes.
   */
  estate: Field[] | null;
}

export type ProfileView =
  | { kind: "loading"; message: string }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      subject: ProfileSubject;
      tabs: ProfileTab[];
      active: ProfileTabKey;
      person: PersonPanels;
      /** Null in a personal workspace, and on an org whose record failed to load. */
      organisation: OrgPanels | null;
      entities: EntityRow[];
      holdings: HoldingRow[];
    };

/**
 * Which tabs exist, and in what order.
 *
 * Two shapes rather than one list with things hidden, because the subject
 * changes what the screen IS. A person's profile is themselves and what they
 * hold; an organisation's is the entity, its people, and — last, because it is
 * a different record — the account of whoever is reading.
 */
export function tabsFor(input: {
  subject: ProfileSubject;
  entities: number;
  holdings: number;
  team: number;
}): ProfileTab[] {
  if (input.subject === "person") {
    return [
      { key: "profile", label: PROFILE_TABS.profile, count: null },
      {
        key: "entities",
        label: PROFILE_TABS.entities,
        count: input.entities + input.holdings,
      },
    ];
  }

  return [
    { key: "organisation", label: PROFILE_TABS.organisation, count: null },
    { key: "team", label: PROFILE_TABS.team, count: input.team },
    { key: "account", label: PROFILE_TABS.account, count: null },
  ];
}

/**
 * The tab to show, given what was asked for.
 *
 * Falls back to the first tab rather than rendering nothing, because the tab
 * key survives a workspace switch: moving from a gallery to a personal
 * workspace with `team` selected would otherwise leave a blank screen with a
 * tab strip that does not contain the selection.
 */
export function activeTab(tabs: ProfileTab[], requested: ProfileTabKey | null): ProfileTabKey {
  const first = tabs[0];
  if (!first) return "profile";
  if (requested && tabs.some((tab) => tab.key === requested)) return requested;
  return first.key;
}

/**
 * The identity card for the person signed in.
 *
 * `meta` is the email rather than the role, unlike the sidebar's card. The
 * sidebar has to say which workspace you are in; here the workspace is not in
 * question and the email is the thing somebody checks when they suspect they
 * are signed in as the wrong account.
 */
export function toPersonIdentity(me: MeResponse): IdentityCard {
  return {
    initials: initialsOf(me.principal.displayName),
    name: me.principal.displayName,
    meta: me.user.email,
    verified: me.principal.verified,
    verifiedLabel: me.principal.verified ? PROFILE_COPY.verified : PROFILE_COPY.unverified,
    fields: [
      { label: PROFILE_COPY.ondoId, value: me.principal.ondoId },
      {
        label: PROFILE_COPY.publicAddress,
        value: me.principal.handle ?? PROFILE_COPY.noHandle,
        absent: me.principal.handle === null,
      },
      { label: PROFILE_COPY.memberSince, value: monthAndYear(me.principal.createdAt) },
    ],
  };
}

export function toOrgIdentity(organisation: OrgProfile): IdentityCard {
  return {
    initials: initialsOf(organisation.displayName),
    name: organisation.displayName,
    meta: ORG_TYPE_LABELS[organisation.orgType],
    verified: organisation.verified,
    verifiedLabel: organisation.verified ? PROFILE_COPY.verified : PROFILE_COPY.unverified,
    fields: [
      { label: PROFILE_COPY.ondoId, value: organisation.ondoId },
      {
        label: PROFILE_COPY.orgRegisteredAddress,
        value: organisation.registeredAddress ?? PROFILE_COPY.notGiven,
        absent: organisation.registeredAddress === null,
      },
      {
        label: PROFILE_COPY.orgRegistrationNumber,
        value: organisation.registrationNumber ?? PROFILE_COPY.notGiven,
        absent: organisation.registrationNumber === null,
      },
      { label: PROFILE_COPY.memberSince, value: monthAndYear(organisation.createdAt) },
    ],
  };
}

/**
 * All eight types, with the declared ones marked.
 *
 * Every one is rendered, not only the chosen ones, because the panel's job is
 * to show what the answer was out of what the question offered — the prototype's
 * grid, and it reads correctly. What is NOT copied from the prototype is its
 * single selection: `domain/membership.ts` says several at once is the normal
 * case, so a grid with one cell lit would misreport a gallery director who also
 * collects.
 */
export function toMembershipChoices(declared: readonly string[]): MembershipChoice[] {
  return MEMBERSHIP_TYPES.map((type) => ({
    label: type.label,
    value: type.value,
    declared: declared.includes(type.value),
  }));
}

/**
 * What this account may still add, and it is never what it may drop.
 *
 * ADD ONLY, which is `ondo.declare_types()`'s rule and is worth restating where
 * the buttons are drawn, because a grid of toggles is the obvious thing to build
 * here and it would be wrong. `collector` is the SOLE route to the collector
 * capacity, so an account that unticked it would keep every collection by RLS
 * and lose every screen that could reach one — a holding stranded behind a
 * checkbox. A correction is somebody asking ONDO, not a click.
 *
 * This is the door an invited account walks through. Somebody asked an address
 * to confirm a movement; the haulier signed in, declared nothing, and reads
 * their verifications. The day they want to register a work of their own, this
 * is what makes them a collector — and `ondo.principal_capacities()` then stops
 * answering `verifier` for them, because the fallback only speaks when nothing
 * else does.
 */
export function addableMemberships(choices: MembershipChoice[]): MembershipChoice[] {
  return choices.filter((choice) => !choice.declared);
}

export function toContactFields(me: MeResponse): Field[] {
  return [
    { label: PROFILE_COPY.nameLabel, value: me.principal.displayName },
    {
      label: PROFILE_COPY.emailLabel,
      value: me.user.email ?? PROFILE_COPY.notGiven,
      absent: me.user.email === null,
    },
    {
      label: PROFILE_COPY.phoneLabel,
      value: me.person.phone ?? PROFILE_COPY.notGiven,
      absent: me.person.phone === null,
    },
    {
      label: PROFILE_COPY.countryLabel,
      value: me.person.country ?? PROFILE_COPY.notGiven,
      absent: me.person.country === null,
    },
  ];
}

/** What the form starts holding. Nulls become empty strings; blanks clear. */
export function toContactValues(me: MeResponse): ContactValues {
  return {
    displayName: me.principal.displayName,
    phone: me.person.phone ?? "",
    country: me.person.country ?? "",
  };
}

/**
 * The three counts the registry can actually stand behind.
 *
 * The prototype's panel has four rows and three of them are gone. Estimated
 * value needs a valuation nothing computes; transfers need an `ondo.transfers`
 * table that `docs/accessFGARules.md` lists under Deliberately absent; fees need
 * a ledger. Showing any of them as £0 or 0 would be a claim about this
 * collection rather than a blank about the product.
 */
export function toSummary(input: {
  collections: CollectionSummary[];
  rooms: number | null;
}): Field[] {
  const owned = input.collections.filter((collection) => collection.level === "owner");
  const works = input.collections.reduce(
    (total, collection) => total + collection.workCount,
    0,
  );

  const fields: Field[] = [
    { label: PROFILE_COPY.summaryWorks, value: String(works) },
    { label: PROFILE_COPY.summaryCollections, value: String(owned.length) },
  ];

  // Null rather than zero when the rooms call has not landed or was refused:
  // "0 viewing rooms" and "we do not know yet" are different sentences, and only
  // one of them should be printed as a number.
  if (input.rooms !== null) {
    fields.push({ label: PROFILE_COPY.summaryRooms, value: String(input.rooms) });
  }

  return fields;
}

/**
 * The three figures an estate's profile states, which OND-80 asked for.
 *
 * "You'll be able to see the number of the works of the catalogue, how many of
 * those have been verified, the number of team members." All three were already
 * worked out one screen away — `toCatalogueView` counts works and attested, and
 * the team roster is on the tab beside this one — and none of the three was
 * stated as a figure anywhere a firm's profile could be screenshotted for a
 * museum's due-diligence request.
 *
 * ATTESTED MEANS ATTESTED AS FAR AS THIS READER CAN SEE, and the ticket is
 * explicit that this is deliberate: `ondo.estate_catalogue()`'s `attested`
 * column is true when a live `verify` attestation exists whose author is a
 * principal the CALLER acts as. A figure meaning "attested by anybody" would be
 * a different number, and it is not this one — so the label says whose word it
 * is rather than leaving the reader to assume it is the world's.
 *
 * Takes counts rather than the rows, because the caller already has them:
 * `toCatalogueView` computes both, and recomputing here would be a second copy
 * of a rule that could drift from the chips on the catalogue screen.
 */
export function toEstateSummary(input: {
  works: number;
  attested: number;
  team: number;
}): Field[] {
  return [
    { label: PROFILE_COPY.estateWorks, value: String(input.works) },
    { label: PROFILE_COPY.estateAttested, value: String(input.attested) },
    { label: PROFILE_COPY.estateTeam, value: String(input.team) },
  ];
}

/**
 * Claims this account has made, and what came back.
 *
 * The status is the outcome in words and never a count. `accessFGARules.md` is
 * explicit that the registry derives no verdict from a tally, and an affiliation
 * is the same kind of record as an attestation: it is shown, and the reader
 * concludes.
 */
export function toAffiliationRows(claims: AffiliationClaim[]): AffiliationRow[] {
  return claims.map((claim) => ({
    claimId: claim.claimId,
    subject: `${claim.subject.displayName} · ${claim.subject.ondoId}`,
    title: claim.title,
    status: AFFILIATION_STANDING_LABELS[affiliationStanding(claim.outcome)],
    tone: affiliationTone(claim),
    // Live and unanswered. A claim somebody has already disputed cannot be
    // withdrawn out from under their answer — the answer is their record.
    canWithdraw: claim.outcome === null && claim.endedAt === null,
    answer: affiliationAnswer(claim),
    audiences: claim.audiences,
    canPublish: claim.canPublish,
    visibility: describeAudiences(claim.audiences),
    claimedFor: claim.claimedForIsOrg ? (claim.claimedFor?.displayName ?? null) : null,
  }));
}

/**
 * What the other party said, when they said anything.
 *
 * BUILT FROM `needsItsAnswerShown` RATHER THAN FROM "is there a body", which is
 * the difference between showing a dispute and showing a dispute that happened
 * to have text. A don't-recall with no body still has to be visible AS a
 * don't-recall — `status` carries that — and this carries the words when there
 * are any. `standsConfirmed` is deliberately not consulted: a confirmation's
 * own note is not an objection and belongs with the row, not under a heading
 * that says the other party pushed back.
 */
function affiliationAnswer(claim: AffiliationClaim): AffiliationRow["answer"] {
  if (!needsItsAnswerShown(claim.outcome)) return null;
  const body = claim.responseBody?.trim();
  if (!body) return null;
  return { body, by: claim.respondedByName };
}

/**
 * Who a relationship is currently shown to, as one sentence.
 *
 * The WIDEST audience that is on, because the three nest — saying "everyone,
 * people on ONDO and people dealing with you" would be three names for one
 * fact. Null audiences mean the server sent none, which is not the same as
 * nothing being published and must not read as hidden.
 */
export function describeAudiences(audiences: AffiliationAudiences | null): string {
  if (!audiences) return "";
  if (audiences.everyone) return AFFILIATION_AUDIENCE_LABELS.everyone;
  if (audiences.ondo_users) return AFFILIATION_AUDIENCE_LABELS.ondo_users;
  if (audiences.dealing_parties) return AFFILIATION_AUDIENCE_LABELS.dealing_parties;
  return PROFILE_COPY.affiliationHidden;
}

/**
 * One audience switched on or off, with the nesting applied.
 *
 * IN THE MODEL AND NOT IN THE VIEW, because it is a rule rather than a spread:
 * turning "everyone" on turns the narrower audiences on, and turning "people on
 * ONDO" off turns "everyone" off — otherwise a reader could untick the audience
 * they meant to exclude and have it silently restored by the wider one still
 * being set. `normaliseAudiences` widens; this is what has to narrow first, and
 * the two together are why a checkbox cannot leave the row saying something the
 * screen does not.
 *
 * `null` in means the caller has no audience to change, which is the inbound
 * read's shape and never a reason to invent the default here.
 */
export function toggleAudience(
  audiences: AffiliationAudiences | null,
  audience: AffiliationAudience,
  enabled: boolean,
): AffiliationAudiences | null {
  if (!audiences) return null;
  if (enabled) return normaliseAudiences({ ...audiences, [audience]: true });

  // Switching one off switches off every audience at least as wide as it.
  const index = AFFILIATION_AUDIENCES.indexOf(audience);
  const narrowed = { ...audiences };
  for (const [position, name] of AFFILIATION_AUDIENCES.entries()) {
    if (position <= index) narrowed[name] = false;
  }
  return narrowed;
}

/**
 * The colour, and the one rule in this file worth stating twice.
 *
 * `verified` is reached through `standsConfirmed` and through nothing else, so
 * there is no outcome — not a don't-recall, not a declined answer, not an
 * unanswered claim — that can be toned as though the other party had agreed.
 * OND-98: never present an unanswered or disputed claim as an unqualified
 * confirmation. A tone is a quieter way of presenting one than a word, which is
 * exactly why it needs the same gate.
 */
function affiliationTone(claim: AffiliationClaim): AffiliationRow["tone"] {
  if (standsConfirmed(claim.outcome)) return "verified";
  if (claim.outcome === "disputed") return "contested";
  if (claim.outcome === null) return "pending";
  return "quiet";
}

/**
 * The organisations this account belongs to.
 *
 * `href` comes from matching the membership against `me.workspaces` rather than
 * from building a path out of the membership itself. A workspace is derived
 * server-side from real grants, so a membership with no workspace is a
 * membership the server decided opens nothing — and a link built anyway would
 * land on a route that resolves to null and bounces.
 */
export function toEntityRows(me: MeResponse): EntityRow[] {
  return me.memberships.map((membership) => {
    const workspace = me.workspaces.find(
      (candidate) => candidate.organisation?.ondoId === membership.organisation.ondoId,
    );

    const parts = [
      ORG_TYPE_LABELS[membership.organisation.orgType],
      membership.title,
      MEMBERSHIP_LEVEL_LABELS[membership.level],
    ].filter((part): part is string => Boolean(part));

    return {
      ondoId: membership.organisation.ondoId,
      name: membership.organisation.displayName,
      initials: initialsOf(membership.organisation.displayName),
      meta: parts.join(" · "),
      verified: membership.organisation.verified,
      href: workspace ? workspacePath(workspace, "profile") : null,
    };
  });
}

export function toHoldingRows(collections: CollectionSummary[]): HoldingRow[] {
  return collections.map((collection) => ({
    id: collection.id,
    name: collection.name,
    holdings: `${collection.workCount} ${collection.workCount === 1 ? "work" : "works"}`,
    level: LEVEL_WORDS[collection.level] ?? collection.level,
  }));
}

const LEVEL_WORDS: Record<string, string> = {
  owner: "Owner",
  curator: "Curator",
  viewer: "Viewer",
};

export function toTeamRows(
  members: OrgMember[],
  readerOndoId?: string,
  /**
   * The firm's own bundle grid, for `isCustomised`.
   *
   * Optional, and an absent grid means no row is marked customised rather than
   * every row being marked. A missing answer must not read as an accusation
   * that somebody edited something.
   */
  bundles: OrgRoleBundleLine[] = [],
): TeamRow[] {
  return members.map((member) => ({
    membershipId: member.membershipId,
    ondoId: member.ondoId,
    name: member.displayName,
    initials: initialsOf(member.displayName),
    title: member.title,
    level: member.level,
    levelLabel: MEMBERSHIP_LEVEL_LABELS[member.level],
    joined: monthAndYear(member.joinedAt),
    canEnd: member.canEnd,
    isYou: member.ondoId === readerOndoId,
    role: member.role,
    roleLabel: member.role ? ORG_ROLE_LABELS[member.role] : PROFILE_COPY.teamNoRole,
    abilities: member.privileges.map((privilege) => ORG_PRIVILEGE_LABELS[privilege]),
    privileges: member.privileges,
    isCustomised: differsFromBundle(member, bundles),
    canSetRole: member.canSetRole,
  }));
}

/**
 * Whether a seat's resolved abilities differ from what its bundle would give.
 *
 * TWO SERVER ANSWERS COMPARED, and not a third opinion about the rule. The
 * resolved list comes from `ondo.seat_privileges()` and the grid from
 * `ondo.org_role_bundles()`; this says only whether they agree.
 *
 * A seat with NO named role is never "customised", even though it may carry an
 * exception. There is no bundle to differ from, so the honest answer is that
 * there is nothing to compare — and marking every such seat would mark most of
 * the roster and mean nothing.
 *
 * The grid is a ROLE's answer and the seat's is a SEAT's, so a disagreement can
 * also come from the floor: a viewer-rung Director does not get `staff_admin`
 * however the bundle reads. That is worth flagging for the same reason an
 * exception is — it is a row whose abilities are not what its role name says.
 */
function differsFromBundle(member: OrgMember, bundles: OrgRoleBundleLine[]): boolean {
  if (member.role === null) return false;

  const forRole = bundles.filter((line) => line.role === member.role);
  if (forRole.length === 0) return false;

  return forRole.some((line) => line.granted !== member.privileges.includes(line.privilege));
}

/**
 * A role select's string value, as a role or as "no named role".
 *
 * IT LIVES HERE AND NOT IN THE VIEW because `"" means null` is a rule and not
 * markup: clearing a seat's role is a real act with a real effect on what that
 * person can do, and a view deciding it would be a business condition in JSX.
 * It also refuses a value that is not one of the four rather than passing it
 * on — a tampered `<option>` is the hostile input this function is tested with,
 * and the server would refuse it anyway.
 */
export function roleFromChoice(value: string): OrgRole | null {
  return isOrgRole(value) ? value : null;
}

/**
 * What the invite form holds while it is being filled in — OND-24.
 *
 * `level` IS A STRING AND NOT A `MembershipLevel`, because a `<select>`'s value
 * is a string and the narrowing is `levelFromChoice`'s job one function down.
 * Typing it as the union here would mean the view casting, which is the business
 * condition in JSX this split exists to prevent.
 */
export type InviteValues = {
  email: string;
  level: string;
  title: string;
};

/** A blank form. `viewer` first, because the mildest rung is the safe default. */
export function emptyInvite(): InviteValues {
  return { email: "", level: "viewer", title: "" };
}

/**
 * The rung a `<select>`'s string names — OND-24.
 *
 * ANYTHING UNRECOGNISED BECOMES `viewer`, which is the safe direction for the
 * same reason `grantFromChoice` clears rather than grants: a value the screen did
 * not understand must not admit a colleague at a rung somebody could not see
 * themselves choosing. The database re-checks the enum regardless; this decides
 * what a confused form means.
 */
export function levelFromChoice(value: string): MembershipLevel {
  return MEMBERSHIP_LEVELS.includes(value as MembershipLevel)
    ? (value as MembershipLevel)
    : "viewer";
}

/**
 * Why this invitation cannot be sent, or null — OND-24.
 *
 * THE ADDRESS IS CHECKED HERE AND NOT ONLY ON THE SERVER, which is not a second
 * authority: the server decides whether the invitation may exist and this
 * decides whether to spend a request finding out. The probe budget is twenty an
 * hour, charged on every call, so a form that posted "sarah@" four times would
 * burn a fifth of a firm's hourly allowance on typos.
 *
 * IT DOES NOT CHECK WHETHER THE ADDRESS IS ALREADY HERE, and could not. Nothing
 * the client holds maps an address to a colleague — `OrgMember` deliberately
 * carries none — and the invitation door resolves nothing either, which is the
 * property that stops an invitation becoming a way to find out who exists. A
 * duplicate is one opaque refusal from the server.
 */
export function validateInvite(values: InviteValues): string | null {
  const email = values.email.trim();
  if (email === "") return PROFILE_COPY.inviteNeedsEmail;

  // Deliberately the loosest useful test rather than a pattern: an address with
  // one @ and something either side is the shape the server's own regex accepts,
  // and a stricter client-side rule refuses real addresses — a colleague at a
  // single-label internal domain, for one.
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return PROFILE_COPY.inviteBadEmail;

  return null;
}

/** One invitation, as the Team screen draws it — OND-24. */
export type InvitationRow = {
  invitationId: string;
  email: string;
  levelLabel: string;
  title: string | null;
  invitedBy: string;
  sent: string;
  /** One sentence about where this invitation has got to. */
  status: string;
  /** True only while it stands, and only where the server says so. */
  canWithdraw: boolean;
};

/**
 * The firm's invitations, newest first, each with one sentence about its state —
 * OND-24.
 *
 * THE STATUS SENTENCE IS COMPUTED HERE RATHER THAN IN THE VIEW, because deciding
 * what a null `deliveredAt` means is exactly the kind of rule that must not sit
 * in JSX. It means three different things — not polled yet, failed, or an address
 * that asked not to be written to and recorded nothing — and the screen may not
 * claim to know which, so an undelivered invitation says only that it has not
 * arrived yet and names the attempt count when there has been one. A sentence
 * guessing "the address may have bounced" would be the screen inventing a fact
 * the database declined to record.
 *
 * AN ANSWERED INVITATION KEEPS ITS ROW, which is the firm's whole answer to "what
 * happened to the invitation I sent" — and the reason OND-24 tells nobody
 * anything (`20260930094000` decision 5: "the row is durable and the screen is
 * honest"). Ordered by the server, which returns them newest first.
 */
export function toInvitationRows(invitations: SeatInvitation[]): InvitationRow[] {
  return invitations.map((invitation) => ({
    invitationId: invitation.invitationId,
    email: invitation.invitedEmail,
    levelLabel: MEMBERSHIP_LEVEL_LABELS[invitation.level],
    title: invitation.title,
    invitedBy: invitation.invitedByName,
    sent: monthAndYear(invitation.createdAt),
    status: describeInvitation(invitation),
    canWithdraw: invitation.canWithdraw,
  }));
}

/** The one sentence. Exported for its own test rather than for a second caller. */
export function describeInvitation(invitation: SeatInvitation): string {
  if (invitation.outcome === "accepted") return PROFILE_COPY.inviteAccepted;
  if (invitation.outcome === "declined") return PROFILE_COPY.inviteDeclined;
  if (invitation.outcome === "withdrawn") return PROFILE_COPY.inviteWithdrawn;

  if (invitation.deliveredAt) return PROFILE_COPY.inviteDelivered;

  // ATTEMPTED AND NOT ARRIVED. The count is named because it is the one fact
  // that distinguishes "no worker has polled yet" from "this has been tried and
  // is not getting through", and an administrator diagnosing a mistyped address
  // has nothing else to go on.
  if (invitation.deliveryAttempts > 0) return PROFILE_COPY.inviteNotDelivered;

  return PROFILE_COPY.invitePending;
}

/**
 * A per-seat ability select's string value, as the three-valued answer the
 * server takes.
 *
 * `"yes"` grants, `"no"` refuses, and ANYTHING ELSE clears — which is the empty
 * option, and is also the safe direction for a value nobody recognises:
 * clearing returns the seat to its bundle, where granting would hand out an
 * ability on a string the screen did not understand.
 */
export function grantFromChoice(value: string): boolean | null {
  if (value === "yes") return true;
  if (value === "no") return false;

  return null;
}

/**
 * The firm's bundle grid, grouped by role, in the order the enum declares.
 *
 * `ORG_ROLES` rather than the rows' own order, so the panel draws four roles
 * even if the server ever answered about fewer — and so two firms' panels read
 * identically.
 */
export function toBundleRows(bundles: OrgRoleBundleLine[]): BundleRow[] {
  return ORG_ROLES.map((role) => ({
    role,
    label: ORG_ROLE_LABELS[role],
    cells: ORG_PRIVILEGES.map((privilege) => {
      const line = bundles.find((l) => l.role === role && l.privilege === privilege);

      return {
        privilege,
        label: ORG_PRIVILEGE_LABELS[privilege],
        // Absent reads as NOT granted. A cell the server did not answer about
        // must not be drawn as an ability somebody holds.
        granted: line?.granted ?? false,
        isEdited: line?.isEdited ?? false,
      };
    }),
  }));
}

/**
 * "March 2023", or an em dash for a date that will not parse.
 *
 * A month rather than a day, everywhere it is used. "Member since 12 March 2023"
 * is a precision nobody asked for on a fact whose only job is to say roughly how
 * long somebody has been here, and the prototype makes the same choice.
 */
export function monthAndYear(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

/**
 * Whether the contact form may be submitted, and why not.
 *
 * Only the display name is required, and only because `ondo.update_person()`
 * refuses an empty one — the constraint is on `principals.display_name` and
 * predates this screen. Phone and country may both be blank: blank is how a
 * field gets cleared under replace semantics, so validating them as required
 * would make the two facts impossible to withdraw.
 */
export function validateContact(values: ContactValues): string | null {
  if (values.displayName.trim() === "") return "Your name cannot be empty.";
  if (values.displayName.trim().length > 120) return "That name is too long.";
  if (values.phone.trim().length > 40) return "That phone number is too long.";
  if (values.country.trim().length > 80) return "That country name is too long.";
  return null;
}

export function validateOrg(values: OrgPanels["values"]): string | null {
  if (values.displayName.trim() === "") return "An organisation needs a name.";
  if (values.displayName.trim().length > 120) return "That name is too long.";
  if (values.registeredAddress.trim().length > 400) return "That address is too long.";
  if (values.registrationNumber.trim().length > 200)
    return "That registration number is too long.";
  return null;
}

/**
 * The ONDO id typed into the affiliation form.
 *
 * Shape only, exactly as the API's `claimAffiliationSchema` is. Whether the id
 * EXISTS is answered by the database, in one message that also covers "that is
 * yourself" and "you already claimed this" — so this refuses 'not-an-id',
 * which is a typo, and says nothing at all about ONDO-9999.
 */
export function validateOndoId(raw: string): string | null {
  return ONDO_ID_PATTERN.test(raw.trim().toUpperCase())
    ? null
    : PROFILE_COPY.affiliationIdInvalid;
}
